import { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageContainer,
  Progress,
  StatutEvaluationBadge,
} from "@repo/ui";
import { useAction, useMutation, useQuery } from "convex/react";
import {
  ArrowLeft,
  ExternalLink,
  FileText,
  Inbox,
  Loader2,
} from "lucide-react";
import {
  EnvoyerLienParcoursDialog,
  useEnvoiLienParcours,
} from "@repo/campagne";
import { SendResultsDialog } from "./ParcoursDetailDialogs";
import { Link, useParams, useSearchParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { ParcoursInfoCard } from "./ParcoursInfoCard";
import { StatutGlobalEnum, TypeEvalEnum } from "@convex/schema";
import { dateCourteHeure, getEvaluationTitle } from "@repo/utils";

type EmailStatus = "idle" | "sending" | "success" | "error";

export function ParcoursDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const backPath = searchParams.get("from") ?? "/campagnes";

  // State pour la modale d'envoi de lien
  // State pour la modale d'envoi des résultats
  const [resultatsModalOpen, setResultatsModalOpen] = useState(false);
  const [resultatsStatus, setResultatsStatus] = useState<EmailStatus>("idle");
  const [resultatsMessage, setResultatsMessage] = useState("");

  // Mutations
  const archiverParcours = useMutation(
    api.parcoursEvaluations.archiverParcours,
  );
  const desarchiverParcours = useMutation(
    api.parcoursEvaluations.desarchiverParcours,
  );

  // Actions
  const envoyerResultatsParcours = useAction(
    api.email.envoyerResultatsParcours,
  );

  // Query
  const result = useQuery(
    api.parcoursEvaluations.obtenirDetailParcoursAdmin,
    id ? { parcoursId: id as Id<"parcoursEvaluations"> } : "skip",
  );

  const envoiLien = useEnvoiLienParcours({
    parcoursId: id as Id<"parcoursEvaluations"> | undefined,
    emailCandidat: result?.parcours.emailCandidat,
  });

  // Handlers
  const handleArchive = async () => {
    if (id) {
      await archiverParcours({ parcoursId: id as Id<"parcoursEvaluations"> });
    }
  };

  const handleDesarchive = async () => {
    if (id) {
      await desarchiverParcours({
        parcoursId: id as Id<"parcoursEvaluations">,
      });
    }
  };

  // Envoyer les résultats
  const handleSendResultats = async () => {
    if (!id) return;
    setResultatsStatus("sending");
    setResultatsMessage("Envoi en cours...");
    try {
      const res = await envoyerResultatsParcours({
        parcoursId: id as Id<"parcoursEvaluations">,
      });
      if (res.succes) {
        setResultatsStatus("success");
        setResultatsMessage(res.message);
      } else {
        setResultatsStatus("error");
        setResultatsMessage(res.message || "Une erreur est survenue.");
      }
    } catch (error) {
      setResultatsStatus("error");
      setResultatsMessage(
        error instanceof Error
          ? error.message
          : "Une erreur inattendue est survenue.",
      );
    }
  };

  const closeResultatsModal = () => {
    setResultatsModalOpen(false);
    setResultatsStatus("idle");
    setResultatsMessage("");
  };

  const getEvaluationIcon = (typeEval: string) => {
    switch (typeEval) {
      case TypeEvalEnum.FORM:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
      case TypeEvalEnum.INBASKET:
        return <Inbox className="h-5 w-5 text-muted-foreground" />;
      case TypeEvalEnum.ENT_STRUCT:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
      default:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getEvaluationDescription = (evaluation: {
    typeEval: string;
    template?: string;
  }) => {
    if (evaluation.typeEval === TypeEvalEnum.FORM) {
      switch (evaluation.template) {
        case "autodiag":
          return "Questionnaire d'auto-évaluation des compétences";
        case "egogramme":
          return "Questionnaire sur le style relationnel";
        case "pami":
          return "Questionnaire de personnalité";
        case "pc-fit":
          return "Classement Q-sort des priorités culturelles";
        case "copsoq3":
          return "Questionnaire sur les conditions de travail";
        default:
          return "Questionnaire à compléter";
      }
    }
    if (evaluation.typeEval === TypeEvalEnum.ENT_STRUCT) {
      return evaluation.template || "Template non défini";
    }
    return "Gestion de boîte de réception simulée";
  };

  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (result === null) {
    return (
      <PageContainer>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">
            Parcours introuvable
          </h1>
          <p className="mt-2 text-muted-foreground">
            Le parcours demandé n'existe pas.
          </p>
          {/* Un vrai lien, pas un `<button>` qui en enrobe un autre : le
              retour est une navigation, elle doit s'ouvrir dans un onglet et
              se copier comme telle. */}
          <Button variant="outline" className="mt-4" asChild>
            <Link to={backPath}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Link>
          </Button>
        </div>
      </PageContainer>
    );
  }

  const { parcours, evaluations, progression } = result;

  const hasAutodiag = evaluations.some(
    (e) => e.typeEval === TypeEvalEnum.FORM && e.template === "autodiag",
  );
  const hasInbasket = evaluations.some(
    (e) => e.typeEval === TypeEvalEnum.INBASKET,
  );
  const peutEnvoyerResultats =
    hasAutodiag && hasInbasket && progression.pourcentage === 100;

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-6">
        <Link
          to={backPath}
          className="text-sm text-muted-foreground hover:underline"
        >
          <ArrowLeft className="h-4 w-4 inline mr-1" />
          Retour
        </Link>
      </div>

      {/* Parcours Info */}
      <ParcoursInfoCard
        parcoursId={id!}
        prenomCandidat={parcours.prenomCandidat}
        nomCandidat={parcours.nomCandidat}
        emailCandidat={parcours.emailCandidat}
        titre={parcours.titre}
        dateCreation={parcours.dateCreation}
        dateModification={parcours.dateModification}
        lienEnvoye={parcours.lienEnvoye}
        archive={parcours.archive}
        anonyme={parcours.anonyme}
        evaluations={evaluations}
        peutEnvoyerResultats={peutEnvoyerResultats}
        onOpenEmailModal={envoiLien.ouvrir}
        onOpenResultatsModal={() => {
          setResultatsStatus("idle");
          setResultatsMessage("");
          setResultatsModalOpen(true);
        }}
        onArchive={handleArchive}
        onDesarchive={handleDesarchive}
      />

      {/* Evaluations List */}
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Évaluations</CardTitle>
              <CardDescription>
                Liste des évaluations du parcours
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 min-w-[140px]">
              <Progress
                value={progression.pourcentage}
                className={`h-2 flex-1 ${progression.pourcentage === 100 ? "[&>div]:bg-etat-fait" : ""}`}
              />
              <span
                className={`text-sm font-medium whitespace-nowrap ${progression.pourcentage === 100 ? "text-etat-fait" : ""}`}
              >
                {progression.pourcentage}%
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {evaluations.map((evaluation) => (
              <div
                key={evaluation.evalId}
                className="flex items-center justify-between p-4 border border-charte-line rounded-surface transition-colors duration-150 hover:border-charte-line-accent"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-surface-3 rounded-controle">
                    {getEvaluationIcon(evaluation.typeEval)}
                  </div>
                  <div>
                    <h3 className="font-medium text-encre">
                      {getEvaluationTitle(evaluation)}
                    </h3>
                    <p className="text-sm text-ardoise">
                      {getEvaluationDescription(evaluation)}
                    </p>
                    {evaluation.dateDebut && (
                      <p className="text-xs text-ardoise mt-1">
                        Commencé le {dateCourteHeure(evaluation.dateDebut)}
                        {evaluation.dateFin &&
                          ` - Terminé le ${dateCourteHeure(evaluation.dateFin)}`}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-end gap-1">
                    <StatutEvaluationBadge
                      statut={evaluation.statut}
                      detailAnalyse
                    />
                  </div>
                  {evaluation.statut === StatutGlobalEnum.TERMINE && (
                    <Link to={`/p/${id}/evaluation/${evaluation.evalId}`}>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Voir les résultats
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <SendResultsDialog
        isOpen={resultatsModalOpen}
        onClose={closeResultatsModal}
        status={resultatsStatus}
        message={resultatsMessage}
        evaluationTitles={evaluations.map((e) => getEvaluationTitle(e))}
        emailCandidat={parcours.emailCandidat}
        onSend={handleSendResultats}
      />

      <EnvoyerLienParcoursDialog
        envoi={envoiLien}
        emailCandidat={parcours.emailCandidat}
        lienEnvoye={parcours.lienEnvoye}
      />
    </PageContainer>
  );
}
